import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Archive, ArrowLeft, Check, CheckCheck, Reply, Send } from 'lucide-react';
import {
  archiveThread,
  getMessageThread,
  markMessageAsRead,
  sendMessageReply
} from '../../api/messagingApi';
import './Messages.css';

interface Message {
  id: number;
  content: string;
  senderId: number;
  senderRole: string;
  senderName: string;
  receiverId: number;
  receiverRole: string;
  isRead: number;
  readAt: string | null;
  createdAt: string;
  learnerClassName?: string;
}

interface Thread {
  id: number;
  subject: string;
  learnerName: string;
  learnerId: number;
  teacherName: string;
  teacherId: number;
  status: string;
  className?: string | null;
  is_archived_by_student?: number;
  is_archived_by_teacher?: number;
}

interface ThreadContext {
  refreshThreads: (options?: { silent?: boolean }) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  activeBox: 'inbox' | 'sent' | 'archived';
}

const formatMessageTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const MessageThreadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const userId = Number(localStorage.getItem('userId') || '0');
  const { refreshThreads, fetchUnreadCount, activeBox } = useOutletContext<ThreadContext>();

  const [thread, setThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sendError, setSendError] = useState('');

  const loadThread = useCallback(async (options?: { silent?: boolean }) => {
    if (!id) {
      return;
    }

    try {
      if (!options?.silent) {
        setLoading(true);
      }

      const data = await getMessageThread(Number(id));
      const nextThread = data.thread || null;
      const nextMessages = data.messages || [];

      setThread(nextThread);
      setMessages(nextMessages);

      const unreadMessages = nextMessages.filter((message: Message) => message.receiverId === userId && message.isRead === 0);
      if (unreadMessages.length > 0) {
        await Promise.all(unreadMessages.map((message: Message) => markMessageAsRead(message.id)));
        await refreshThreads({ silent: true });
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
      if (!options?.silent) {
        navigate('/messages');
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [fetchUnreadCount, id, navigate, refreshThreads, userId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadThread({ silent: true });
    }, 8000);

    return () => window.clearInterval(intervalId);
  }, [id, loadThread]);

  const handleReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id || !replyContent.trim()) {
      return;
    }

    try {
      setSending(true);
      setSendError('');
      await sendMessageReply({
        threadId: Number(id),
        content: replyContent.trim()
      });
      setReplyContent('');
      await loadThread();
      await refreshThreads();
      await fetchUnreadCount();
    } catch (error: any) {
      console.error('Error sending reply:', error);
      setSendError(error?.response?.data?.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!thread || !id) {
      return;
    }

    const currentlyArchived =
      role === 'student'
        ? Boolean(thread.is_archived_by_student)
        : Boolean(thread.is_archived_by_teacher);

    try {
      setArchiving(true);
      await archiveThread(Number(id), !currentlyArchived);
      await refreshThreads();
      navigate('/messages');
    } catch (error) {
      console.error('Error archiving thread:', error);
    } finally {
      setArchiving(false);
    }
  };

  const recipientSummary = useMemo(() => {
    if (!thread) {
      return 'Conversation';
    }

    if (role === 'student') {
      return thread.teacherName || 'School staff';
    }

    return thread.learnerName || 'Student';
  }, [role, thread]);

  if (loading) {
    return (
      <div className="thread-state-card">
        <div className="pro-loader" aria-label="Loading" />
        <p>Loading conversation…</p>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="thread-state-card">
        <p>Conversation not found.</p>
      </div>
    );
  }

  const isArchived =
    role === 'student'
      ? Boolean(thread.is_archived_by_student)
      : Boolean(thread.is_archived_by_teacher);
  const participantType = role === 'student' ? 'Staff conversation' : 'Student conversation';

  return (
    <div className="thread-view">
      <header className="thread-view-header">
        <div className="thread-view-heading">
          <button type="button" className="icon-action-btn mobile-only" onClick={() => navigate('/messages')}>
            <ArrowLeft size={18} />
          </button>
          <div className="thread-view-summary">
            <p className="section-eyebrow">Thread details</p>
            <h2>{thread.subject || 'No subject'}</h2>
            <div className="thread-view-meta">
              <span>{participantType}</span>
              <span>{messages.length} message{messages.length === 1 ? '' : 's'}</span>
            </div>
            <div className="thread-participant-line">
              <span>With {recipientSummary}</span>
              {thread.className ? <span>{thread.className}</span> : null}
              <span>{thread.status}</span>
            </div>
          </div>
        </div>

        <button type="button" className="secondary-btn" onClick={handleArchiveToggle} disabled={archiving}>
          <Archive size={16} />
          <span>{archiving ? 'Saving...' : isArchived || activeBox === 'archived' ? 'Unarchive' : 'Archive'}</span>
        </button>
      </header>

      <div className="thread-messages">
        {messages.map((message) => {
          const isOwnMessage = message.senderId === userId;
          return (
            <article
              key={message.id}
              className={`thread-message-card ${isOwnMessage ? 'own' : 'incoming'}`}
            >
              <div className="thread-message-meta">
                <strong>{isOwnMessage ? 'You' : message.senderName}</strong>
                <span>{formatMessageTimestamp(message.createdAt)}</span>
              </div>
              <p>{message.content}</p>
              <div className="thread-message-footer">
                <span>{message.receiverRole === 'student' ? 'Sent to student' : 'Sent to staff'}</span>
                {isOwnMessage ? (
                  <span className="message-read-state">
                    {message.isRead ? <CheckCheck size={14} /> : <Check size={14} />}
                    <span>{message.isRead ? 'Read' : 'Sent'}</span>
                  </span>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      <form className="thread-reply-box" onSubmit={handleReply}>
        <div className="thread-reply-header">
          <div>
            <p className="section-eyebrow">Reply</p>
            <h3>Continue this conversation</h3>
          </div>
          <Reply size={18} />
        </div>

        <textarea
          value={replyContent}
          onChange={(event) => setReplyContent(event.target.value)}
          placeholder="Write your reply here..."
          rows={4}
        />
        {sendError ? <div className="compose-feedback error">{sendError}</div> : null}
        <div className="thread-reply-actions">
          <span>Replies stay within this thread for both participants.</span>
          <button type="submit" className="primary-btn" disabled={sending || !replyContent.trim()}>
            <Send size={16} />
            <span>{sending ? 'Sending...' : 'Send Reply'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageThreadPage;
